import { apiFetcher } from "@/services/API.service";
import { NewTask, ShortAppUser, ShortAppUserSchema } from "@/types";
import { validateArrayWithSchema } from "@/utils/dataValidators.zod";

// #region createTask
// Create task
export async function createTask(task: NewTask): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/task/create",
      method: "POST",
      options: {
        body: {
          assigned_to: task.assigned_to,
          title: task.title,
          due_date: task.due_date,
          input: task.input,
          collaborators: task.collaborators,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 400) {
      return { rStatus: "error", message: "bad request" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}
// #endregion createTask

// #region fetchAllUsers
// Fetch all users
export async function fetchAllUsers({
  retry = false,
  signal,
}: {
  retry?: boolean;
  signal?: AbortSignal;
}): Promise<
  | {
      rStatus: "success";
      data: ShortAppUser[];
    }
  | {
      rStatus: "error";
      message: string;
    }
> {
  try {
    if (retry) {
      console.log("Retrying fetchAllUsers");
    }

    const response = await apiFetcher({
      route: "/users/get",
      method: "POST",
      signal: signal,
    });

    console.log("fetchAllUsers response", response);

    if (response.status === 200) {
      const validatedData = validateArrayWithSchema<ShortAppUser>(
        response.data,
        ShortAppUserSchema,
        "ShortAppUser"
      );

      if (!validatedData) {
        return {
          rStatus: "error",
          message: "invalid response: data is not an array of valid users",
        };
      }

      return {
        rStatus: "success",
        data: validatedData,
      };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", message: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", message: "route not found" };
    }

    if (response.status === 0) {
      if (response.error === "request_aborted") {
        return { rStatus: "error", message: "request_aborted" };
      }

      if (!retry) {
        return fetchAllUsers({ retry: true });
      }

      return {
        rStatus: "error",
        message: "network_error",
      };
    }

    return { rStatus: "error", message: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", message: "failed to fetch" };
  }
}

export async function sendNewUserProfileImage(imageBase64: string): Promise<
  | {
      rStatus: "success";
    }
  | {
      rStatus: "error";
      rMessage: string;
    }
> {
  try {
    const response = await apiFetcher({
      route: "/users/update/profile/image",
      method: "POST",
      options: {
        body: {
          image: imageBase64,
        },
      },
    });

    if (response.status === 200 || response.status === 202) {
      return { rStatus: "success" };
    }

    if (response.status === 401 || response.status === 403) {
      return { rStatus: "error", rMessage: "unauthorized" };
    }

    if (response.status === 404) {
      return { rStatus: "error", rMessage: "route not found" };
    }

    return { rStatus: "error", rMessage: "failed to fetch" };
  } catch (error) {
    console.error(error);
    return { rStatus: "error", rMessage: "failed to fetch" };
  }
}
